import React, { FC, useEffect, useRef, useState } from 'react';
import { Modal } from '@clearcover/atlas';

import { AgentPaymentPreferenceModal } from 'QuoteApp/components/AgentPaymentPreferenceModal';
import { useAppContext } from 'QuoteApp/services/providers';
import { PaymentPlanComparisonPanel } from 'QuoteApp/screens/Purchase/PriceCustomizer/PaymentPlanComparisonPanel';
import {
  buildFlexiblePaymentPreferencesPayload,
  getPriceCustomizerInitialValues,
} from 'QuoteApp/screens/Purchase/PriceCustomizer/services';
import { Asset } from 'Shared/components';

import styles from './styles.module.css';

type PaymentPlanComparisonModalProps = {
  open: boolean;
  onClose: () => void;
  onSettingsClick?: () => void;
};

const GRID_CONCEPT_LABELS = {
  downpayment: 'DUE TODAY',
  installment: 'FUTURE PAYMENTS',
  total: 'TOTAL PREMIUM',
} as const;

export const PaymentPlanComparisonModal: FC<PaymentPlanComparisonModalProps> = ({
  open,
  onClose,
  onSettingsClick,
}) => {
  const [
    isAgentPaymentPreferenceModalOpen,
    setAgentPaymentPreferenceModalOpen,
  ] = useState(false);
  const {
    rate,
    details,
    config,
    rateLoading,
    detailsLoading,
    agentPreferredPaymentFrequency,
    isAgentPreferredPaymentFrequencyLoading,
    fetchRate,
    refreshDetails,
    loadAgentPreferredPaymentFrequency,
    api,
  } = useAppContext();

  const modalContentRef = useRef<HTMLDivElement>(null);

  const getInitialValues = () =>
    getPriceCustomizerInitialValues({
      details,
      config,
    });

  const isLoading = rateLoading || detailsLoading || api.loading;
  const openAgentPaymentPreferenceModal = async () => {
    onSettingsClick?.();

    try {
      await loadAgentPreferredPaymentFrequency();
    } catch {
      // keep modal flow available even if preference query fails
    } finally {
      setAgentPaymentPreferenceModalOpen(true);
    }
  };
  const closeAgentPaymentPreferenceModal = () =>
    setAgentPaymentPreferenceModalOpen(false);

  useEffect(() => {
    const atlasModal = modalContentRef.current?.parentElement;

    if (!open || !atlasModal) {
      return;
    }

    atlasModal.classList.add(styles.modalContainer);

    return () => {
      atlasModal.classList.remove(styles.modalContainer);
    };
  }, [open]);

  useEffect(() => {
    // Temporary workaround: Atlas Modal does not support close-on-backdrop-click.
    // Remove this effect once Atlas provides native backdrop close behavior.
    const modalOverlay = modalContentRef.current?.closest(
      '[data-testid="openModal"]'
    );

    if (!open || !modalOverlay) {
      return;
    }

    const handleOverlayClick = (event: Event) => {
      if (event.target === modalOverlay && !isAgentPaymentPreferenceModalOpen) {
        onClose();
      }
    };

    modalOverlay.addEventListener('click', handleOverlayClick);

    return () => {
      modalOverlay.removeEventListener('click', handleOverlayClick);
    };
  }, [isAgentPaymentPreferenceModalOpen, onClose, open]);

  return (
    <Modal
      controlled
      open={open}
      showCloseIcon={false}
      handleClose={onClose}
      variant="xl"
    >
      {closeModal => (
        <div
          className={styles.content}
          data-testid="payment-plan-comparison-modal"
          ref={modalContentRef}
        >
          <div className={styles.header}>
            <div>
              <h2 className={styles.title}>Choose a payment frequency</h2>
              <p className={styles.subtitle}>
                Most customers prefer a payment frequency that matches the
                frequency of when they get paid. You&apos;ll be able to choose
                their specific due date at checkout.
              </p>
            </div>

            <button
              type="button"
              className={styles.settingsButton}
              data-testid="payment-plan-comparison-settings-button"
              onClick={openAgentPaymentPreferenceModal}
            >
              <Asset variant="settings" />
              Settings
            </button>
          </div>

          <div className={styles.planComparisonContainer}>
            <PaymentPlanComparisonPanel
              rate={rate}
              isLoading={isLoading}
              selectedLabel="Current"
              variant="grid"
              conceptLabels={GRID_CONCEPT_LABELS}
              onPlanSelect={async frequencyName => {
                await api.saveAppData({
                  data: buildFlexiblePaymentPreferencesPayload({
                    ...getInitialValues(),
                    frequency: frequencyName,
                  }),
                });
                await fetchRate();
                refreshDetails();
                closeModal();
              }}
            />
          </div>

          {isAgentPaymentPreferenceModalOpen && (
            <AgentPaymentPreferenceModal
              open
              rate={rate}
              initialFrequency={agentPreferredPaymentFrequency}
              isInitialFrequencyLoading={
                isAgentPreferredPaymentFrequencyLoading
              }
              onClose={closeAgentPaymentPreferenceModal}
              onSaveAgentPreference={async frequency => {
                await api.saveAgentPreferredPaymentFrequency(frequency);
                closeAgentPaymentPreferenceModal();
              }}
            />
          )}
        </div>
      )}
    </Modal>
  );
};
